import { ReactKeycloakProvider } from '@react-keycloak/web'
import { MenuAction, UserActionType } from '../state/user-state'
import Keycloak from 'keycloak-js'
import { AuthClientInitOptions, AuthClientEvent } from '@react-keycloak/core'
import { Dispatch, useCallback, useContext } from 'react'
import { User } from '../../types/user-types'
import { fakeAuthEnabled } from './auth-utils'
import { getTokenAuthToken } from './auth-utils'

enum KEYCLOAK_EVENTS {
  ONREADY = 'onReady',
  ONAUTHREFRESHSUCCESS = 'onAuthRefreshSuccess',
  ONAUTHLOGOUT = 'onAuthLogout',
  ONTOKENEXPIRED = 'onTokenExpired',
}
const SET_TOKEN_EVENTS: AuthClientEvent[] = [KEYCLOAK_EVENTS.ONREADY, KEYCLOAK_EVENTS.ONAUTHREFRESHSUCCESS]
const CLEAR_TOKEN_EVENTS: AuthClientEvent[] = [KEYCLOAK_EVENTS.ONAUTHLOGOUT]
const REFRESH_TOKEN_EVENTS: AuthClientEvent[] = [KEYCLOAK_EVENTS.ONTOKENEXPIRED]

const INIT_OPTIONS: AuthClientInitOptions = {
  pkceMethod: 'S256',
  onLoad: 'check-sso',
}

const REFRESH_WITHIN_VALIDITY = 30

class KeycloakSingleton {
  // No trailing slash on Keycloak-js adapter url
  // https://www.keycloak.org/docs/latest/securing_apps/#_javascript_adapter
  private static _keycloakConfig = {
    url: `${process.env.REACT_APP_KEYCLOAK_URL}/auth`,
    clientId: process.env.REACT_APP_FRONTEND_CLIENT_ID ?? 'react',
    realm: process.env.REACT_APP_MAIN_REALM ?? '',
  }
  private static _instance: Keycloak | null

  private constructor() {}

  private static _initialiseKeycloak(): Keycloak {
    const keycloakAdapterSettings = Object.values(this._keycloakConfig)
    for (const setting of keycloakAdapterSettings) {
      if (setting === undefined && process.env.NODE_ENV !== 'test') {
        throw new Error('Keycloak adapter not configured correctly')
      }
    }

    const keycloak = new Keycloak(this._keycloakConfig)
    return keycloak
  }

  public static get instance(): Keycloak {
    if (!KeycloakSingleton._instance) {
      KeycloakSingleton._instance = KeycloakSingleton._initialiseKeycloak()
    }
    return KeycloakSingleton._instance
  }
}

interface KeycloakWrapperProps {
  userContext: React.Context<{
    state: User
    dispatch: Dispatch<MenuAction>
    isLoading: boolean
  }>
}

export default function KeyCloakWrapper({ children, userContext }: React.PropsWithChildren<KeycloakWrapperProps>) {
  const keycloak = KeycloakSingleton.instance
  const { dispatch } = useContext(userContext)
  const processEvents = useCallback(
    (event: AuthClientEvent) => {
      if (fakeAuthEnabled() || getTokenAuthToken()) {
        return
      }

      if (event === KEYCLOAK_EVENTS.ONREADY && !keycloak?.token) {
        // Keycloak has looked for a token, but not found it
        dispatch({ type: UserActionType.SET_TOKEN, payload: { token: false } })
      }

      if (SET_TOKEN_EVENTS.includes(event)) {
        dispatch({ type: UserActionType.SET_TOKEN, payload: { token: keycloak.token } })
      }

      if (CLEAR_TOKEN_EVENTS.includes(event)) {
        dispatch({ type: UserActionType.SET_TOKEN, payload: { token: undefined } })
      }

      if (REFRESH_TOKEN_EVENTS.includes(event)) {
        keycloak.updateToken(REFRESH_WITHIN_VALIDITY).catch(() => {
          // Handle token refresh error by refreshing the page with the login modal open
          window.location.search = '?login=true'
        })
      }
    },
    [dispatch, keycloak],
  )

  if (!keycloak) {
    throw new Error('Keycloak could not be initialised')
  }

  return (
    <ReactKeycloakProvider
      initOptions={INIT_OPTIONS}
      authClient={keycloak}
      onEvent={processEvents}
      autoRefreshToken={false}
    >
      {children}
    </ReactKeycloakProvider>
  )
}
